import {Node, Edge} from 'reactflow';

import {DefaultTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/DefaultTaskSchemaNodeAdapter';
import {
  TaskSchemaStaticNodeData,
  TaskSchemaNodeData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {TaskSchemaNodeRowRenderer} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRowRenderer';
import {SimpleBenchlingNodeHandle} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';

import {
  ProcessFlowchartNodeType,
  NodeTransitionPosition,
  FlowchartStaticEdgeData,
  NodeTransitionPositionConfig,
} from './TransitionModalWithFlowchartEditorConstants';

/**
 * Initial node positions within a transition.
 *
 * TODO: Use layout algorithm to determine node positions
 */
function createNodePosition(nodeTransitionPositionConfig: NodeTransitionPositionConfig): {
  x: number;
  y: number;
} {
  const {nodeTransitionPosition, indexInNodeTransitionPosition} = nodeTransitionPositionConfig;
  const isSourceNodeInTransition = nodeTransitionPosition === NodeTransitionPosition.SOURCE;

  const initialRowSpacing = 200;
  const initialColumnSpacing = 400;
  const initialPosition = 0;

  return {
    x: initialPosition + (isSourceNodeInTransition ? initialPosition : initialColumnSpacing),
    y:
      initialPosition +
      (isSourceNodeInTransition ? indexInNodeTransitionPosition * initialRowSpacing : initialPosition),
  };
}
interface FlowchartOptions {
  onNodeHeaderClick: (nodeId: string) => void;
  onFieldClick: (nodeId: string, fieldId: string) => void;
}

// Exported for tests only
export function createNode(
  staticData: TaskSchemaStaticNodeData,
  nodeTransitionPositionConfig: NodeTransitionPositionConfig,
  options: FlowchartOptions
): Node<TaskSchemaNodeData> {
  // TODO: Use different adapters based on task schema type
  const adapter = new DefaultTaskSchemaNodeAdapter({});
  return {
    id: staticData.id,
    type: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    position: createNodePosition(nodeTransitionPositionConfig),
    data: {
      ...staticData,
      onNodeHeaderClick: options.onNodeHeaderClick,
      nodeDetailsSidepanelContent: (node: Node) => adapter.nodeDetailsSidepanelContent(node),
      onFieldClick: options.onFieldClick,
      rowRenderer: TaskSchemaNodeRowRenderer,
      nodeHeaderHandleRenderer: SimpleBenchlingNodeHandle,
    },
  };
}

interface FlowchartData {
  sourceNodes: Array<Node<TaskSchemaNodeData>>;
  targetNode: Node<TaskSchemaNodeData> | null;
  edges: Array<Edge>;
}

interface BuildFlowchartFromTransitionDataRunnerArgs {
  sourceNodesStaticData: Array<TaskSchemaStaticNodeData>;
  targetNodeStaticData: TaskSchemaStaticNodeData | null;
  edgesData: Array<FlowchartStaticEdgeData>;
  options: FlowchartOptions;
}

class BuildFlowchartFromTransitionDataRunner {
  private readonly sourceNodesStaticData: Array<TaskSchemaStaticNodeData>;
  private readonly targetNodeStaticData: TaskSchemaStaticNodeData | null;
  private readonly edgesData: Array<FlowchartStaticEdgeData>;
  private readonly options: FlowchartOptions;
  private sourceNodes: Array<Node>;
  private targetNode: Node | null;
  private edges: Array<Edge>;

  constructor(args: BuildFlowchartFromTransitionDataRunnerArgs) {
    this.sourceNodesStaticData = args.sourceNodesStaticData;
    this.targetNodeStaticData = args.targetNodeStaticData;
    this.edgesData = args.edgesData;
    this.options = args.options;
    this.sourceNodes = [];
    this.targetNode = null;
    this.edges = [];
  }

  run(): FlowchartData {
    this.convertStaticDataToNodes(this.options);
    this.convertStaticDataToEdges(this.options);
    return {
      sourceNodes: this.sourceNodes,
      targetNode: this.targetNode,
      edges: this.edges,
    };
  }

  private convertStaticDataToNodes(options: FlowchartOptions): void {
    this.sourceNodes = this.sourceNodesStaticData.map((sourceStaticData, index) =>
      createNode(
        sourceStaticData,
        {
          nodeTransitionPosition: NodeTransitionPosition.SOURCE,
          indexInNodeTransitionPosition: index,
        },
        options
      )
    );

    this.targetNode = this.targetNodeStaticData
      ? createNode(
          this.targetNodeStaticData,
          {
            nodeTransitionPosition: NodeTransitionPosition.TARGET,
            indexInNodeTransitionPosition: 0,
          },
          options
        )
      : null;
  }

  private convertStaticDataToEdges(options: FlowchartOptions): void {
    this.edges = this.edgesData;
  }
}

export default function buildFlowchartFromTransitionData(
  args: BuildFlowchartFromTransitionDataRunnerArgs
): FlowchartData {
  const runner = new BuildFlowchartFromTransitionDataRunner(args);
  return runner.run();
}
